import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { ApiResponse } from 'apisauce';
import { RobocloudDTO } from '@robocorp/robocloud-common/lib/types';
import { OkAction, ErrorAction } from './baseActions';
import { secretService } from '../services/secretsAPI';
import { Secret } from '../types/resources';
import { formatArrToJson } from '../utils/formatKeyValuePairHelper';
import {
  encrypt,
  decrypt,
  generateRSAKeyPair,
  exportRSAPublicKey,
} from '../utils/transitEncryptionHelper';
import { formatApiErrorPayload } from './utils';
import { selectCurrentWorkspaceId } from './utils/selectors';

type AppState = import('~/store').AppState;

export enum SecretActionType {
  SILENT_GET_SECRET_LIST_ITEMS = 'SILENT_GET_SECRET_LIST_ITEMS',
  GET_SECRET_LIST_ITEMS = 'GET_SECRET_LIST_ITEMS',
  GET_SECRET_LIST_ITEMS_OK = 'GET_SECRET_LIST_ITEMS_OK',
  GET_SECRET_LIST_ITEMS_ERROR = 'GET_SECRET_LIST_ITEMS_ERROR',
  GET_SECRET = 'GET_SECRET',
  GET_SECRET_OK = 'GET_SECRET_OK',
  GET_SECRET_ERROR = 'GET_SECRET_ERROR',
  DELETE_SECRET = 'DELETE_SECRET',
  DELETE_SECRET_OK = 'DELETE_SECRET_OK',
  DELETE_SECRET_ERROR = 'DELETE_SECRET_ERROR',
  PUT_SECRET = 'PUT_SECRET',
  PUT_SECRET_OK = 'PUT_SECRET_OK',
  PUT_SECRET_ERROR = 'PUT_SECRET_ERROR',
}

type WithSecretName = { name: string };

type GetSecretListItemsAction =
  | Action<SecretActionType.SILENT_GET_SECRET_LIST_ITEMS>
  | Action<SecretActionType.GET_SECRET_LIST_ITEMS>
  | OkAction<SecretActionType.GET_SECRET_LIST_ITEMS_OK, RobocloudDTO.SecretListItem[]>
  | ErrorAction<SecretActionType.GET_SECRET_LIST_ITEMS_ERROR>;

type GetSecretAction =
  | (Action<SecretActionType.GET_SECRET> & WithSecretName)
  | (OkAction<SecretActionType.GET_SECRET_OK, RobocloudDTO.Secret> & WithSecretName)
  | (ErrorAction<SecretActionType.GET_SECRET_ERROR> & WithSecretName);

type DeleteSecretAction =
  | (Action<SecretActionType.DELETE_SECRET> & WithSecretName)
  | (OkAction<SecretActionType.DELETE_SECRET_OK, RobocloudDTO.Secret> & WithSecretName)
  | (ErrorAction<SecretActionType.DELETE_SECRET_ERROR> & WithSecretName);

type PutSecretAction =
  | (Action<SecretActionType.PUT_SECRET> & WithSecretName)
  | (OkAction<SecretActionType.PUT_SECRET_OK, RobocloudDTO.Secret> & WithSecretName)
  | (ErrorAction<SecretActionType.PUT_SECRET_ERROR> & WithSecretName);

export type SecretActions =
  | GetSecretListItemsAction
  | GetSecretAction
  | DeleteSecretAction
  | PutSecretAction;

export const getSecretListItems =
  (): ThunkAction<void, AppState, null, GetSecretListItemsAction> => async (dispatch, getState) => {
    if (getState().workspaceResources.secrets.allNames.length > 0) {
      dispatch({ type: SecretActionType.SILENT_GET_SECRET_LIST_ITEMS });
    } else {
      dispatch({ type: SecretActionType.GET_SECRET_LIST_ITEMS });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await secretService.getSecretListItems({ workspaceId });
    if (response.ok) {
      dispatch({
        type: SecretActionType.GET_SECRET_LIST_ITEMS_OK,
        data: response.data as RobocloudDTO.SecretListItem[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: SecretActionType.GET_SECRET_LIST_ITEMS_ERROR,
      });
    }
  };

export const getSecretByName =
  (name: string): ThunkAction<void, AppState, null, GetSecretAction> =>
  async (dispatch, getState) => {
    dispatch({ type: SecretActionType.GET_SECRET, name });

    const keyPair: CryptoKeyPair = await generateRSAKeyPair();
    const publicKey = await exportRSAPublicKey(keyPair.publicKey);
    const encryptionScheme = RobocloudDTO.EncryptionSchemes.V2;

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await secretService.getSecretByName({
      workspaceId,
      name,
      publicKey,
      encryptionScheme,
    });

    if (response.ok && response.data) {
      const secret: RobocloudDTO.Secret = await decrypt(response.data, keyPair.privateKey);

      dispatch({
        name,
        type: SecretActionType.GET_SECRET_OK,
        data: secret,
      });
    } else {
      const error = response.problem ? response.problem : 'Unexpected problem while getting secret';

      dispatch({
        name,
        type: SecretActionType.GET_SECRET_ERROR,
        error,
      });
    }
  };

export const deleteSecretByName =
  (
    name: string,
  ): ThunkAction<Promise<ApiResponse<RobocloudDTO.Secret>>, AppState, null, DeleteSecretAction> =>
  async (dispatch, getState) => {
    dispatch({ type: SecretActionType.DELETE_SECRET, name });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await secretService.deleteSecretByName({ workspaceId, name });
    if (response.ok) {
      dispatch({
        name,
        type: SecretActionType.DELETE_SECRET_OK,
        data: response.data as RobocloudDTO.Secret,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: SecretActionType.DELETE_SECRET_ERROR,
        name,
      });
    }

    return response;
  };

export const putSecretByName =
  (name: string, update: Secret): ThunkAction<void, AppState, null, PutSecretAction> =>
  async (dispatch, getState) => {
    dispatch({
      name,
      type: SecretActionType.PUT_SECRET,
    });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const secret = getState().workspaceResources.secrets.byName[name];
    const formattedValue = update.value && formatArrToJson(update.value);
    const { value, encryption } = await encrypt({ workspaceId }, formattedValue);

    const updated = {
      ...secret,
      ...update,
      value,
      encryption,
    } as RobocloudDTO.EncryptedSecretV2;

    const response = await secretService.putSecretByName({
      workspaceId,
      name,
      updateDetails: updated,
    });
    if (response.ok) {
      dispatch({
        name,
        type: SecretActionType.PUT_SECRET_OK,
        data: response.data as RobocloudDTO.Secret,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: SecretActionType.PUT_SECRET_ERROR,
        name,
      });
    }
  };
